"use client";

import { Download, Mail, Plus, RotateCcw, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

import { generateQuotePDF } from "@/lib/pdf-generator";
import { useEmailQuote } from "@/lib/queries/use-email-quote";
import { isValidEmail } from "@/lib/utils";

interface LineItem {
  id: string;
  description: string;
  quantity: string;
  unit: string;
  unitPrice: string;
}

interface QuotationData {
  companyName: string;
  companyAddress: string;
  companyPhone: string;
  companyEmail: string;
  clientName: string;
  clientAddress: string;
  clientPhone: string;
  clientEmail: string;
  quoteNumber: string;
  quoteDate: string;
  validityDate: string;
  taxRate: string;
  notes: string;
  lineItems: LineItem[];
}

const INITIAL_STATE: QuotationData = {
  companyName: "Your Company",
  companyAddress: "123 Main Street, City, State 12345",
  companyPhone: "(555) 123-4567",
  companyEmail: "contact@company.com",
  clientName: "",
  clientAddress: "",
  clientPhone: "",
  clientEmail: "",
  quoteNumber: "Q-" + new Date().getFullYear() + "-001",
  quoteDate: new Date().toISOString().split("T")[0],
  validityDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
  taxRate: "7",
  notes: "",
  lineItems: [
    {
      id: "1",
      description: "Foundation excavation & site preparation",
      quantity: "500",
      unit: "sq ft",
      unitPrice: "12.00",
    },
    {
      id: "2",
      description: "Concrete pouring - Grade A",
      quantity: "200",
      unit: "sq ft",
      unitPrice: "25.00",
    },
  ],
};

export function Home() {
  const [data, setData] = useState<QuotationData>(INITIAL_STATE);
  const [saved, setSaved] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailToSend, setEmailToSend] = useState("");
  const emailMutation = useEmailQuote();

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem("quotation-draft");
    if (saved) {
      try {
        setData(JSON.parse(saved));
      } catch (e) {
        console.error("Failed to load saved quotation", e);
      }
    }
  }, []);

  // Save to localStorage whenever data changes
  useEffect(() => {
    localStorage.setItem("quotation-draft", JSON.stringify(data));
    setSaved(true);
    const timer = setTimeout(() => setSaved(false), 2000);
    return () => clearTimeout(timer);
  }, [data]);

  const handleInputChange = (field: keyof QuotationData, value: string) => {
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const handleLineItemChange = (id: string, field: keyof LineItem, value: string) => {
    setData((prev) => ({
      ...prev,
      lineItems: prev.lineItems.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      ),
    }));
  };

  const addLineItem = () => {
    const newId = Date.now().toString();
    setData((prev) => ({
      ...prev,
      lineItems: [
        ...prev.lineItems,
        {
          id: newId,
          description: "",
          quantity: "",
          unit: "",
          unitPrice: "",
        },
      ],
    }));
  };

  const removeLineItem = (id: string) => {
    setData((prev) => ({
      ...prev,
      lineItems: prev.lineItems.filter((item) => item.id !== id),
    }));
  };

  const calculateLineTotal = (quantity: string, unitPrice: string): number => {
    const qty = parseFloat(quantity) || 0;
    const price = parseFloat(unitPrice) || 0;
    return qty * price;
  };

  const subtotal = data.lineItems.reduce(
    (sum, item) => sum + calculateLineTotal(item.quantity, item.unitPrice),
    0
  );

  const taxAmount = (subtotal * (parseFloat(data.taxRate) || 0)) / 100;
  const total = subtotal + taxAmount;

  const handleDownloadPDF = () => {
    generateQuotePDF(data, subtotal, taxAmount, total);
  };

  const handleSendEmail = () => {
    if (!isValidEmail(emailToSend)) {
      return;
    }

    emailMutation.mutate(
      {
        clientEmail: emailToSend,
        quoteData: data,
        subtotal,
        taxAmount,
        total,
      },
      {
        onSuccess: () => {
          setShowEmailModal(false);
          setEmailToSend("");
        },
      }
    );
  };

  const handleClearForm = () => {
    if (window.confirm("Are you sure you want to clear the entire form?")) {
      setData(INITIAL_STATE);
      localStorage.removeItem("quotation-draft");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-primary text-primary-foreground border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-accent text-accent-foreground flex items-center justify-center font-bold text-xl">
              🏗
            </div>
            <div>
              <h1 className="text-2xl font-bold">QuoteBuild</h1>
              <p className="text-xs text-primary-foreground/70 tracking-widest">
                CONSTRUCTION QUOTATIONS
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {saved && <span className="text-xs text-primary-foreground/70">Auto-saved</span>}
            <button
              onClick={handleClearForm}
              className="p-2.5 hover:bg-primary-foreground/10 rounded-lg transition-colors"
              title="Clear all data"
            >
              <RotateCcw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Company and Client Details */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Company Details */}
          <div className="card-section">
            <h2 className="text-lg font-bold text-foreground mb-5 flex items-center gap-3">
              <span className="w-7 h-7 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-xs font-bold">
                1
              </span>
              Your Company
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">
                  Company Name
                </label>
                <input
                  type="text"
                  className="input-field"
                  value={data.companyName}
                  onChange={(e) => handleInputChange("companyName", e.target.value)}
                  placeholder="Your Company"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">
                  Address
                </label>
                <input
                  type="text"
                  className="input-field"
                  value={data.companyAddress}
                  onChange={(e) => handleInputChange("companyAddress", e.target.value)}
                  placeholder="123 Main Street, City, State"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">
                    Phone
                  </label>
                  <input
                    type="text"
                    className="input-field"
                    value={data.companyPhone}
                    onChange={(e) => handleInputChange("companyPhone", e.target.value)}
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">
                    Email
                  </label>
                  <input
                    type="email"
                    className="input-field"
                    value={data.companyEmail}
                    onChange={(e) => handleInputChange("companyEmail", e.target.value)}
                    placeholder="contact@company.com"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Client Details */}
          <div className="card-section">
            <h2 className="text-lg font-bold text-foreground mb-5 flex items-center gap-3">
              <span className="w-7 h-7 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-xs font-bold">
                2
              </span>
              Client Details
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">
                  Client Name
                </label>
                <input
                  type="text"
                  className="input-field"
                  value={data.clientName}
                  onChange={(e) => handleInputChange("clientName", e.target.value)}
                  placeholder="Client Name"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">
                  Address
                </label>
                <input
                  type="text"
                  className="input-field"
                  value={data.clientAddress}
                  onChange={(e) => handleInputChange("clientAddress", e.target.value)}
                  placeholder="Client Address"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">
                    Phone
                  </label>
                  <input
                    type="text"
                    className="input-field"
                    value={data.clientPhone}
                    onChange={(e) => handleInputChange("clientPhone", e.target.value)}
                    placeholder="Phone"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">
                    Email
                  </label>
                  <input
                    type="email"
                    className="input-field"
                    value={data.clientEmail}
                    onChange={(e) => handleInputChange("clientEmail", e.target.value)}
                    placeholder="Email"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quote Metadata */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <div className="card-section">
            <label className="block text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
              Quote Number
            </label>
            <input
              type="text"
              className="input-field"
              value={data.quoteNumber}
              onChange={(e) => handleInputChange("quoteNumber", e.target.value)}
            />
          </div>
          <div className="card-section">
            <label className="block text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
              Quote Date
            </label>
            <input
              type="date"
              className="input-field"
              value={data.quoteDate}
              onChange={(e) => handleInputChange("quoteDate", e.target.value)}
            />
          </div>
          <div className="card-section">
            <label className="block text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
              Valid Until
            </label>
            <input
              type="date"
              className="input-field"
              value={data.validityDate}
              onChange={(e) => handleInputChange("validityDate", e.target.value)}
            />
          </div>
          <div className="card-section">
            <label className="block text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
              Tax Rate (%)
            </label>
            <input
              type="number"
              className="input-field"
              value={data.taxRate}
              onChange={(e) => handleInputChange("taxRate", e.target.value)}
              step="0.1"
              min="0"
            />
          </div>
        </div>

        {/* Line Items */}
        <div className="card-section mb-8">
          <h2 className="text-lg font-bold text-foreground mb-5 flex items-center gap-3">
            <span className="w-7 h-7 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-xs font-bold">
              3
            </span>
            Line Items
          </h2>

          {/* Table Header */}
          <div className="hidden md:grid md:grid-cols-12 gap-3 mb-2 px-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            <div className="col-span-5 px-3">Description</div>
            <div className="col-span-2 px-3 text-center">Quantity</div>
            <div className="col-span-2 px-3 text-right">Unit Price</div>
            <div className="col-span-2 px-3 text-right">Total</div>
            <div className="col-span-1"></div>
          </div>

          {/* Table Body */}
          <div className="space-y-3">
            {data.lineItems.map((item) => {
              const lineTotal = calculateLineTotal(item.quantity, item.unitPrice);
              return (
                <div
                  key={item.id}
                  className="hidden md:grid md:grid-cols-12 gap-3 items-center py-3 md:py-0 px-1 hover:bg-background transition-colors"
                >
                  <input
                    type="text"
                    className="col-span-5 input-field px-3"
                    placeholder="Description (e.g., Foundation excavation)"
                    value={item.description}
                    onChange={(e) => handleLineItemChange(item.id, "description", e.target.value)}
                  />
                  <input
                    type="text"
                    className="col-span-2 input-field px-3 text-center"
                    placeholder="Qty"
                    value={item.quantity}
                    onChange={(e) => handleLineItemChange(item.id, "quantity", e.target.value)}
                  />
                  <input
                    type="number"
                    className="col-span-2 input-field px-3 text-right font-['JetBrains_Mono']"
                    placeholder="0.00"
                    value={item.unitPrice}
                    onChange={(e) => handleLineItemChange(item.id, "unitPrice", e.target.value)}
                    step="0.01"
                    min="0"
                  />
                  <div className="col-span-2 px-3 text-right font-semibold font-['JetBrains_Mono'] text-foreground">
                    ${lineTotal.toFixed(2)}
                  </div>
                  <button
                    onClick={() => removeLineItem(item.id)}
                    className="col-span-1 w-8 h-8 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 flex items-center justify-center transition-colors"
                    title="Remove item"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}

            {/* Mobile View */}
            <div className="md:hidden space-y-4">
              {data.lineItems.map((item) => {
                const lineTotal = calculateLineTotal(item.quantity, item.unitPrice);
                return (
                  <div key={item.id} className="bg-background p-4 rounded-lg border border-border">
                    <div className="space-y-3">
                      <input
                        type="text"
                        className="input-field"
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) =>
                          handleLineItemChange(item.id, "description", e.target.value)
                        }
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <input
                          type="text"
                          className="input-field"
                          placeholder="Qty"
                          value={item.quantity}
                          onChange={(e) =>
                            handleLineItemChange(item.id, "quantity", e.target.value)
                          }
                        />
                        <input
                          type="number"
                          className="input-field"
                          placeholder="Unit Price"
                          value={item.unitPrice}
                          onChange={(e) =>
                            handleLineItemChange(item.id, "unitPrice", e.target.value)
                          }
                          step="0.01"
                          min="0"
                        />
                      </div>
                      <div className="flex justify-between items-center pt-2">
                        <span className="text-sm font-semibold">
                          Total: ${lineTotal.toFixed(2)}
                        </span>
                        <button
                          onClick={() => removeLineItem(item.id)}
                          className="p-2 bg-red-50 text-red-500 hover:bg-red-100 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Add Line Item Button */}
          <button
            onClick={addLineItem}
            className="mt-6 w-full py-3 rounded-lg border-2 border-dashed border-border hover:border-accent hover:bg-background transition-colors text-foreground font-semibold flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Line Item
          </button>
        </div>

        {/* Notes and Summary */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Notes */}
          <div className="md:col-span-2 card-section">
            <h3 className="text-lg font-bold text-foreground mb-4">Terms & Notes</h3>
            <textarea
              className="input-field w-full min-h-40"
              placeholder="Add any special terms, conditions, or notes for this quotation..."
              value={data.notes}
              onChange={(e) => handleInputChange("notes", e.target.value)}
            />
          </div>

          {/* Summary */}
          <div className="card-section h-fit">
            <h3 className="text-lg font-bold text-foreground mb-5">Summary</h3>

            <div className="space-y-4 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="font-['JetBrains_Mono'] font-semibold">
                  ${subtotal.toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Tax</span>
                  <input
                    type="number"
                    className="w-16 px-2 py-1 rounded border border-border text-xs text-center font-['JetBrains_Mono']"
                    value={data.taxRate}
                    onChange={(e) => handleInputChange("taxRate", e.target.value)}
                    step="0.1"
                    min="0"
                  />
                  <span className="text-muted-foreground">%</span>
                </div>
                <span className="font-['JetBrains_Mono'] font-semibold">
                  ${taxAmount.toFixed(2)}
                </span>
              </div>

              <div className="border-t border-border pt-4 mt-4">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-foreground">Total</span>
                  <span className="text-2xl font-bold font-['JetBrains_Mono'] text-accent">
                    ${total.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-3">
              <button
                onClick={handleDownloadPDF}
                className="w-full btn-primary bg-primary text-primary-foreground flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download PDF
              </button>

              <button
                onClick={() => setShowEmailModal(true)}
                disabled={!data.clientEmail}
                className="w-full btn-secondary bg-accent text-accent-foreground flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                title={!data.clientEmail ? "Enter client email first" : "Send quote via email"}
              >
                <Mail className="w-4 h-4" />
                Email Quote
              </button>
            </div>

            {/* Email Modal */}
            {showEmailModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                <div className="bg-card rounded-lg shadow-lg p-6 max-w-md w-full mx-4">
                  <h3 className="text-lg font-bold text-foreground mb-4">Email Quote</h3>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Recipient Email
                      </label>
                      <input
                        type="email"
                        value={emailToSend}
                        onChange={(e) => setEmailToSend(e.target.value)}
                        placeholder={data.clientEmail || "client@example.com"}
                        className="w-full px-3 py-2 rounded border border-border bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && isValidEmail(emailToSend)) {
                            handleSendEmail();
                          }
                        }}
                      />
                      {emailToSend && !isValidEmail(emailToSend) && (
                        <p className="text-sm text-destructive mt-1">
                          Please enter a valid email address
                        </p>
                      )}
                    </div>

                    <div className="flex gap-3 justify-end pt-4">
                      <button
                        onClick={() => {
                          setShowEmailModal(false);
                          setEmailToSend("");
                        }}
                        className="px-4 py-2 rounded border border-border hover:bg-muted text-foreground"
                        disabled={emailMutation.isPending}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSendEmail}
                        disabled={!isValidEmail(emailToSend) || emailMutation.isPending}
                        className="px-4 py-2 rounded bg-accent text-accent-foreground hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {emailMutation.isPending ? "Sending..." : "Send"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
